import React from "react";
import PropTypes from "prop-types";
import { RCL as useTranslation } from "../../RCL";
import { Helmet } from "react-helmet";
import * as styles from "./PageHeader.module.scss";
import { Heading, HeadingSize } from "@sunwing/wvi-components";
import { StarRating } from "../../StarRating";
import { GuestReview } from "../GuestReview/GuestReview";

const PageHeaderConnector = ({ hotelData }) => {
	const translations = {
		metaTitle: useTranslation({ searchKey: "seo-wvi-hotel-metal-title" }),
		metaDescription: useTranslation({ searchKey: "seo-wvi-hotel-meta-description" }),
		adultsOnly: useTranslation({ searchKey: "adults-only" }),
		beach: useTranslation({ searchKey: "beach" }),
		value: useTranslation({ searchKey: "value" }),
		room: useTranslation({ searchKey: "room" }),
		entertainment: useTranslation({ searchKey: "entertainment" }),
		dining: useTranslation({ searchKey: "dining" }),
		pool: useTranslation({ searchKey: "pool" }),
		service: useTranslation({ searchKey: "service" }),
		basedOnNum: useTranslation({ searchKey: "based-on-numreviews-reviews" }),
		guestReviewLabel: useTranslation({ searchKey: "guest-review" }),
	};
	// Header data
	const data = {
		title: hotelData.HotelName,
		rating: parseFloat(hotelData.StarRating ?? 0),
		csqRating: parseFloat(hotelData.GuestExperience?.OverallRating?.[0]?.OverallScore ?? 0),
		numberOfReviews: hotelData.GuestExperience?.NumberOfReviews?.[0]?.NumberOfReviews,
		requiredAge: parseInt(hotelData.Adults?.[0]?.MinimumGuestAge, 10),
		country: hotelData.RegionName,
		destination: hotelData.DestinationName,

		// Ratings
		diningRating: parseFloat(hotelData.GuestExperience?.SectionRating?.[0]?.Dining ?? 0),
		poolRating: parseFloat(hotelData.GuestExperience?.SectionRating?.[0]?.Pool ?? 0),
		roomRating: parseFloat(hotelData.GuestExperience?.SectionRating?.[0]?.Accommodations ?? 0),
		beachRating: parseFloat(hotelData.GuestExperience?.SectionRating?.[0]?.Beach ?? 0),
		entertainmentRating: parseFloat(
			hotelData.GuestExperience?.SectionRating?.[0]?.Entertainment ?? 0
		),
		serviceRating: parseFloat(hotelData.GuestExperience?.SectionRating?.[0]?.Service ?? 0),
		valueRating: parseFloat(hotelData.GuestExperience?.SectionRating?.[0]?.Value ?? 0),
		hotelChain: hotelData.HotelChain,

		overallRating: parseFloat(hotelData.GuestExperience?.OverallRating?.[0]?.OverallScore ?? 0),
		overallRatingDescription: hotelData.GuestExperience?.OverallRating?.[0]?.OverallDescription,

		adultsOnly: hotelData.ResortOverview?.AdultsOnly,
	};

	const checkHotelChain = hotelCode => {
		const validCodes = [4, 5, 37];
		return validCodes.includes(hotelCode);
	};

	return (
		<div className={styles?.pageHeader}>
			<div>
				{data.title && (
					<>
						<Helmet>
							<title>{translations.metaTitle?.replace("{{hotelName}}", data.title)}</title>
							<meta
								name="description"
								content={translations.metaDescription?.replace("{{hotelName}}", data.title)}
							/>
							<meta
								name="robot"
								content={checkHotelChain ? "Index, Follow" : "No Index, No Follow"}
							/>
						</Helmet>
						<div className={styles?.titleSection}>
							{data.country && (
								<Heading
									className={styles?.titleSection__subtitle}
									Tag="div"
									size={HeadingSize.eyebrow}>
									{data.destination ? `${data.destination}, ${data.country}` : data.country}
								</Heading>
							)}
							<Heading Tag="h2" size={HeadingSize.lg}>
								{data.title}
							</Heading>
						</div>
					</>
				)}
			</div>

			<div className={styles?.detailsSection}>
				{data.rating && (
					<span className={styles?.titleSection__stars}>
						{data.rating && <StarRating rating={data.rating} />}
					</span>
				)}
				{!!data.numberOfReviews && (
					<div className={styles?.reviewsContainer}>
						<span className={styles?.reviewsLabel}>
							{translations.guestReviewLabel} {data.overallRating} {data.overallRatingDescription}
						</span>
						<div className={styles?.ratingsBreakdownContainer}>
							<GuestReview data={data} translations={translations} />
						</div>
					</div>
				)}
				{data.adultsOnly && <span>{translations.adultsOnly}</span>}

				{data.requiredAge ? <span className={styles?.spacer}>|</span> : null}

				{data.requiredAge ? (
					<span className={styles?.detail}>
						{data.requiredAge >= 16
							? `${translations.adultsOnly} (${data.requiredAge}+)`
							: `${data.requiredAge}+`}
					</span>
				) : null}
			</div>
		</div>
	);
};

PageHeaderConnector.propTypes = {
	hotelData: PropTypes.object,
};

PageHeaderConnector.defaultProps = {
	hotelData: {},
};

export default PageHeaderConnector;
export { PageHeaderConnector };
